/**
 * Guest upload page — /g/{slug}.
 * Entirely config-driven: everything tenant-specific comes from the API.
 */

import "./styles.css";
import { api, isMockMode } from "./api";
import { resolveCaptionStyle, resolveFrameAspectRatio, type PropertyConfig } from "./types";
import {
  MAX_FILES,
  MAX_CAPTION_LENGTH,
  normalizeCaption,
  validateGuestSubmission,
  validateSelection,
  type FileKind
} from "./lib/validation";
import { attributionName, composeStripText, shouldBurnIn } from "./lib/caption";
import { computeFramefillDimensions, computeTargetDimensions, coverCrop } from "./lib/resize";
import { decodeImage, releaseSource, sourceSize } from "./lib/decode";
import { drawCaption } from "./lib/burnin";
import { heicToJpeg } from "./lib/heic";
import { processFile, type ProcessedFile } from "./lib/pipeline";

/* ---------------------------------------------------------------- helpers */

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element #${id}`);
  return found as T;
}

const screens = {
  loading: el<HTMLElement>("screen-loading"),
  notfound: el<HTMLElement>("screen-notfound"),
  error: el<HTMLElement>("screen-error"),
  form: el<HTMLElement>("screen-form"),
  uploading: el<HTMLElement>("screen-uploading"),
  done: el<HTMLElement>("screen-done")
};

function showScreen(name: keyof typeof screens): void {
  for (const [key, section] of Object.entries(screens)) {
    section.hidden = key !== name;
  }
  window.scrollTo({ top: 0 });
}

/* ------------------------------------------------------------------ state */

interface SelectedItem {
  id: number;
  file: File;
  kind: Exclude<FileKind, "unsupported" | "unsupported-video">;
  /** Object URL for thumbnail (image or video). Null while HEIC converts. */
  thumbUrl: string | null;
  /** Cached HEIC→JPEG conversion, reused by preview and upload pipeline. */
  jpegPromise: Promise<Blob> | null;
  /** Cached decoded source for the live preview (first image only). */
  previewSource: ImageBitmap | HTMLImageElement | null;
}

let config: PropertyConfig | null = null;
let items: SelectedItem[] = [];
let nextItemId = 1;
let slug = "";

/* --------------------------------------------------------------- elements */

const propertyName = el<HTMLHeadingElement>("property-name");
const fileInput = el<HTMLInputElement>("file-input");
const addFilesBtn = el<HTMLButtonElement>("add-files");
const pickerHint = el<HTMLParagraphElement>("picker-hint");
const thumbStrip = el<HTMLUListElement>("thumb-strip");
const fileErrors = el<HTMLDivElement>("file-errors");
const previewWrap = el<HTMLDivElement>("preview-wrap");
const previewCanvas = el<HTMLCanvasElement>("preview-canvas");
const nameInput = el<HTMLInputElement>("guest-name");
const captionInput = el<HTMLTextAreaElement>("caption");
const captionCount = el<HTMLSpanElement>("caption-count");
const emailInput = el<HTMLInputElement>("email");
const phoneInput = el<HTMLInputElement>("phone");
const consentInput = el<HTMLInputElement>("consent");
const consentCopy = el<HTMLSpanElement>("consent-copy");
const privacyLink = el<HTMLAnchorElement>("privacy-link");
const incentiveLine = el<HTMLParagraphElement>("incentive-line");
const form = el<HTMLFormElement>("guest-form");
const formError = el<HTMLDivElement>("form-error");
const submitButton = el<HTMLButtonElement>("submit-button");
const uploadList = el<HTMLUListElement>("upload-list");
const uploadError = el<HTMLDivElement>("upload-error");
const uploadRetry = el<HTMLButtonElement>("upload-retry");
const doneHeading = el<HTMLHeadingElement>("done-heading");
const doneIncentive = el<HTMLParagraphElement>("done-incentive");
const donePrivacyLink = el<HTMLAnchorElement>("done-privacy-link");
const shareMore = el<HTMLButtonElement>("share-more");
const retryConfig = el<HTMLButtonElement>("retry-config");

/* ------------------------------------------------------------ config load */

function parseSlug(pathname: string): string | null {
  const match = pathname.match(/^\/g\/([^/]+)\/?$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
}

/** /g/{slug}/privacy — the anonymous privacy notice + guest self-service page. */
function parsePrivacySlug(pathname: string): string | null {
  const match = pathname.match(/^\/g\/([^/]+)\/privacy\/?$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]!);
  } catch {
    return null;
  }
}

async function loadConfig(): Promise<void> {
  showScreen("loading");
  try {
    const loaded = await api.fetchConfig(slug);
    if (!loaded) {
      showScreen("notfound");
      return;
    }
    config = loaded;
    applyConfig(loaded);
    showScreen("form");
  } catch {
    showScreen("error");
  }
}

function applyConfig(cfg: PropertyConfig): void {
  document.title = `${cfg.name} — Guestbook`;
  propertyName.textContent = cfg.name;
  consentCopy.textContent =
    `Yes — ${cfg.name} may use the email or phone above to tell me about future ` +
    `stays and offers. Add at least one so they can reach you. Unticked means no ` +
    `marketing; you can opt out anytime.`;
  const incentive = cfg.incentiveText.trim();
  incentiveLine.hidden = incentive === "";
  incentiveLine.textContent = incentive;
  const privacyHref = `/g/${encodeURIComponent(slug)}/privacy`;
  privacyLink.href = privacyHref;
  donePrivacyLink.href = privacyHref; // "Manage your info" on the confirmation screen (#130)
  pickerHint.textContent = `Up to ${MAX_FILES} photos or videos — we’ll spruce them up for the frame.`;
}

/* -------------------------------------------------------- file selection */

function setErrors(container: HTMLElement, messages: string[]): void {
  container.replaceChildren(
    ...messages.map((message) => {
      const p = document.createElement("p");
      p.textContent = message;
      return p;
    })
  );
}

function addFiles(picked: File[]): void {
  const { accepted, rejected } = validateSelection(items.length, picked);
  setErrors(fileErrors, rejected.map((r) => r.reason));

  for (const entry of accepted) {
    const item: SelectedItem = {
      id: nextItemId++,
      file: entry.file,
      kind: entry.kind,
      thumbUrl: null,
      jpegPromise: null,
      previewSource: null
    };
    if (entry.kind === "heic") {
      // Convert in the background so Chrome/Android can show a thumbnail.
      item.jpegPromise = heicToJpeg(entry.file);
      item.jpegPromise
        .then((jpeg) => {
          if (!items.includes(item)) return; // removed meanwhile
          item.thumbUrl = URL.createObjectURL(jpeg);
          renderThumbs();
          void schedulePreview();
        })
        .catch(() => {
          removeItem(item.id);
          setErrors(fileErrors, [
            `We couldn’t read “${item.file.name}” — please try a different photo.`
          ]);
        });
    } else {
      item.thumbUrl = URL.createObjectURL(entry.file);
    }
    items.push(item);
  }

  renderThumbs();
  void schedulePreview();
}

function removeItem(id: number): void {
  const item = items.find((i) => i.id === id);
  if (!item) return;
  if (item.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
  if (item.previewSource) releaseSource(item.previewSource);
  items = items.filter((i) => i.id !== id);
  renderThumbs();
  void schedulePreview();
}

function renderThumbs(): void {
  thumbStrip.replaceChildren(
    ...items.map((item) => {
      const li = document.createElement("li");
      li.className = "thumb";

      if (item.thumbUrl && item.kind === "video") {
        const video = document.createElement("video");
        video.src = item.thumbUrl;
        video.muted = true;
        video.playsInline = true;
        video.preload = "metadata";
        li.append(video);
        const badge = document.createElement("span");
        badge.className = "badge";
        badge.textContent = "VIDEO";
        li.append(badge);
      } else if (item.thumbUrl) {
        const img = document.createElement("img");
        img.src = item.thumbUrl;
        img.alt = "";
        li.append(img);
      } else {
        const pending = document.createElement("span");
        pending.className = "pending";
        pending.textContent = "Preparing…";
        li.append(pending);
      }

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "remove";
      remove.setAttribute("aria-label", "Remove this file");
      remove.textContent = "×";
      remove.addEventListener("click", () => removeItem(item.id));
      li.append(remove);
      return li;
    })
  );
}

/* ------------------------------------------------------------ live preview */

let previewSeq = 0;
let previewTimer: number | undefined;

function schedulePreviewSoon(): void {
  window.clearTimeout(previewTimer);
  previewTimer = window.setTimeout(() => void schedulePreview(), 130);
}

async function schedulePreview(): Promise<void> {
  const seq = ++previewSeq;
  if (!config?.captionBurnIn) {
    previewWrap.hidden = true;
    return;
  }
  const first = items.find((i) => i.kind !== "video");
  if (!first) {
    previewWrap.hidden = true;
    return;
  }

  try {
    if (!first.previewSource) {
      const blob: Blob = first.kind === "heic" ? await (first.jpegPromise as Promise<Blob>) : first.file;
      const source = await decodeImage(blob);
      if (seq !== previewSeq || !items.includes(first)) return;
      first.previewSource = source;
    }
    const source = first.previewSource;
    const { width, height } = sourceSize(source);

    // Mirror the upload pipeline EXACTLY so preview == upload for the active
    // style: 'framefill' composes at the frame aspect (photo cover-cropped to
    // fill it) when there is a caption; otherwise the photo's own aspect.
    const style = resolveCaptionStyle(config?.captionStyle);
    const caption = captionInput.value;
    const strip = shouldBurnIn(true, caption)
      ? composeStripText(caption, nameInput.value, new Date())
      : null;
    const framefill = style === "framefill" && strip !== null;
    const dims = framefill
      ? computeFramefillDimensions(width, height, resolveFrameAspectRatio(config?.frameAspect), 720)
      : computeTargetDimensions(width, height, 720);

    previewCanvas.width = dims.width;
    previewCanvas.height = dims.height;
    const ctx = previewCanvas.getContext("2d");
    if (!ctx) return;
    if (framefill) {
      const crop = coverCrop(width, height, dims.width, dims.height);
      ctx.drawImage(source, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, dims.width, dims.height);
    } else {
      ctx.drawImage(source, 0, 0, dims.width, dims.height);
    }

    if (strip) {
      drawCaption(ctx, dims.width, dims.height, strip, style);
    }
    previewWrap.hidden = false;
  } catch {
    previewWrap.hidden = true;
  }
}

/* ------------------------------------------------------------ submit flow */

interface UploadRow {
  status: HTMLElement;
  fill: HTMLElement;
}

function buildUploadRows(): UploadRow[] {
  const rows: UploadRow[] = [];
  uploadList.replaceChildren(
    ...items.map((item, index) => {
      const li = document.createElement("li");
      li.className = "upload-row";

      if (item.thumbUrl && item.kind !== "video") {
        const img = document.createElement("img");
        img.className = "mini";
        img.src = item.thumbUrl;
        img.alt = "";
        li.append(img);
      } else {
        const box = document.createElement("div");
        box.className = "mini";
        li.append(box);
      }

      const meta = document.createElement("div");
      meta.className = "meta";
      const name = document.createElement("div");
      name.className = "name";
      name.textContent =
        item.kind === "video" ? `Video ${index + 1}` : `Photo ${index + 1}`;
      const status = document.createElement("div");
      status.className = "status";
      status.textContent = "Waiting…";
      const bar = document.createElement("div");
      bar.className = "bar";
      const fill = document.createElement("div");
      fill.className = "bar-fill";
      bar.append(fill);
      meta.append(name, status, bar);
      li.append(meta);

      rows.push({ status, fill });
      return li;
    })
  );
  return rows;
}

function rowStatus(row: UploadRow, text: string, cls?: "ok" | "err"): void {
  row.status.textContent = text;
  row.status.className = `status${cls ? ` ${cls}` : ""}`;
}

function rowProgress(row: UploadRow, fraction: number | "indeterminate"): void {
  if (fraction === "indeterminate") {
    row.fill.className = "bar-fill indeterminate";
    return;
  }
  row.fill.className = "bar-fill";
  row.fill.style.width = `${Math.round(fraction * 100)}%`;
}

function validateForm(): string[] {
  // Everything the API would reject is caught here, BEFORE any blob upload,
  // so a guest never uploads and then hits a wall (WI #44/#47/#50).
  return validateGuestSubmission({
    name: nameInput.value,
    email: emailInput.value,
    phone: phoneInput.value,
    consent: consentInput.checked,
    fileCount: items.length
  });
}

let submitting = false;

async function handleSubmit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  if (submitting || !config) return;

  const problems = validateForm();
  setErrors(formError, problems);
  if (problems.length > 0) {
    formError.scrollIntoView({ block: "center", behavior: "smooth" });
    return;
  }

  submitting = true;
  submitButton.disabled = true;
  uploadError.replaceChildren();
  uploadRetry.hidden = true;
  showScreen("uploading");
  const rows = buildUploadRows();

  const guestName = nameInput.value.trim();
  const caption = normalizeCaption(captionInput.value);
  const burnIn = shouldBurnIn(config.captionBurnIn, caption);

  try {
    // 1. Pipeline: HEIC→JPEG, downscale/re-encode, caption burn-in.
    const processed: ProcessedFile[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      const row = rows[i]!;
      rowStatus(row, item.kind === "video" ? "Getting ready…" : "Optimizing for the frame…");
      rowProgress(row, "indeterminate");
      const preConverted = item.jpegPromise ? await item.jpegPromise : undefined;
      const result = await processFile(
        item.file,
        item.kind,
        i,
        {
          burnIn,
          caption,
          guestName,
          date: new Date(),
          captionStyle: resolveCaptionStyle(config.captionStyle),
          frameAspect: config.frameAspect
        },
        preConverted
      );
      processed.push(result);
      rowStatus(row, "Ready to send");
      rowProgress(row, 0);
    }

    // 2. Session: get SAS upload URLs.
    const session = await api.createSession(
      slug,
      processed.map((p) => ({ name: p.name, type: p.type, size: p.blob.size }))
    );
    if (session.uploads.length !== processed.length) {
      throw new Error("Upload session mismatch");
    }

    // 3. Parallel direct-to-blob uploads with per-file progress.
    await Promise.all(
      processed.map(async (p, i) => {
        const row = rows[i]!;
        rowStatus(row, "Uploading…");
        await api.uploadToSas(session.uploads[i]!.sasUrl, p.blob, p.type, (fraction) =>
          rowProgress(row, fraction)
        );
        rowStatus(row, "Done", "ok");
      })
    );

    // 4. Finalize the submission.
    await api.submit(slug, {
      submissionId: session.submissionId,
      name: guestName,
      caption,
      email: emailInput.value.trim() || undefined,
      phone: phoneInput.value.trim() || undefined,
      consent: consentInput.checked
    });

    showDone(guestName);
  } catch {
    setErrors(uploadError, [
      "Something went wrong while sending. Your photos are still here — give it another try."
    ]);
    uploadRetry.hidden = false;
  } finally {
    submitting = false;
    submitButton.disabled = false;
  }
}

function showDone(guestName: string): void {
  const first = attributionName(guestName);
  doneHeading.textContent = first ? `Thanks, ${first}!` : "Thanks!";
  const incentive = (config?.incentiveText ?? "").trim();
  doneIncentive.hidden = incentive === "";
  doneIncentive.textContent = incentive;
  showScreen("done");
}

function resetForAnotherRound(): void {
  for (const item of [...items]) removeItem(item.id);
  captionInput.value = "";
  captionCount.textContent = "0";
  setErrors(fileErrors, []);
  setErrors(formError, []);
  showScreen("form");
}

/* ------------------------------------------------------------------ wiring */

addFilesBtn.addEventListener("click", () => fileInput.click());
fileInput.addEventListener("change", () => {
  addFiles(Array.from(fileInput.files ?? []));
  fileInput.value = "";
});

captionInput.addEventListener("input", () => {
  captionCount.textContent = String(
    Math.min(captionInput.value.length, MAX_CAPTION_LENGTH)
  );
  schedulePreviewSoon();
});
nameInput.addEventListener("input", schedulePreviewSoon);

form.addEventListener("submit", (event) => void handleSubmit(event));
uploadRetry.addEventListener("click", () => showScreen("form"));
shareMore.addEventListener("click", resetForAnotherRound);
retryConfig.addEventListener("click", () => void loadConfig());

/* ------------------------------------------------------------------- boot */

const privacySlug = parsePrivacySlug(window.location.pathname);
const parsed = parseSlug(window.location.pathname);

if (privacySlug) {
  // Anonymous privacy notice + self-service. Lazy-loaded so the upload page
  // never ships this code; it takes over #app and returns.
  void import("./privacy/privacy-page").then((m) => m.renderPrivacyPage(privacySlug));
} else if (!parsed && isMockMode && window.location.pathname === "/") {
  // Dev convenience: mock mode at the root jumps to a sample slug.
  window.location.replace("/g/sample-guesthouse");
} else if (!parsed) {
  showScreen("notfound");
} else {
  slug = parsed;
  void loadConfig();
}
